import React, { useEffect, useState, useRef } from "react";
import { Grid, Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import {
  getMasterData,
  getStateMasterData,
  getDistrictMasterData,
  getCityMasterData,
} from "../../../services/master/master_service";
import {getMasterData as getChoicesMasterData} from "../../../services/choices/choices_services";
import { getAllMasterData } from "../../../services/master/master_service";
import { getLandingPages, checkRuleOverlap, getUserCountForRule } from "../../../services/landing_page/landing_page";
import SnackbarComp from "../../notification/components/snackbar";
import { ContentInformationSection } from "./rules-form/content-information-section";
import { RulesSection } from "./rules-form/rules-section";
import { RulesRankingList } from "./rules-form/rules-ranking-list";
import { UserCountDisplay } from "./rules-form/user-count-display";

interface RulesFormData {
  name?: string | null;
  description?: string | null;
  id?: number | null;
  content_id?: number | null;
  min_age?: number | null;
  max_age?: number | null;
  gender?: string | null;
  occupation?: number | null;
  state?: number | null;
  district?: number |string | null;
  locality?: string | null;
  pincode?: string | null;
  areas_of_interest?: number[];
  area_of_residence?: number | null;
  caste?: number | null;
  working_status?: number | null;
  family_income?: number | null;
  education_qualification?: number | null;
  work_experience?: number | string | null;
  differently_abled?: boolean | string | null;
  minority_group?: boolean |string | null;
  student?: boolean |string | null;
  bpl_category?: boolean |string | null;
}

interface RulesPayload {
  name: string;
  description: string;
  content_id: number;
  rules: {
    gender?: string;
    occupation?: number;
    state?: number;
    district?: number;
    caste?: number;
    working_status?: number;
    family_income?: number;
    education_qualification?: number;
    work_experience?: number;
    area_of_residence?: number;
    areas_of_interest?: number[];
    locality?: string;
    pincode?: string;
    differently_abled?: boolean;
    minority_group?: boolean;
    student?: boolean;
    bpl_category?: boolean;
    min_age?: number;
    max_age?: number;
  };
  rules_meta: {
    gender?: string;
    occupation?: string;
    state?: string;
    district?: string;
    caste?: string;
    working_status?: string;
    family_income?: string;
    education_qualification?: string;
    work_experience?: string;
    area_of_residence?: string;
    areas_of_interest?: string[];
    locality?: string;
    pincode?: string;
    differently_abled?: boolean;
    minority_group?: boolean;
    student?: boolean;
    bpl_category?: boolean;
    min_age?: number;
    max_age?: number;
  };
}

interface RulesCreationFormProps {
  viewMode?: boolean;
  initialData?: RulesFormData | null;
  onDataChange: (data: RulesPayload) => void;
  handleSave: () => void;
  currentRuleId?: number | null; // ID of the rule being edited
}

export const RulesCreationForm: React.FC<RulesCreationFormProps> = ({
  viewMode = false,
  initialData = null,
  onDataChange,
  handleSave,
  currentRuleId = null
}) => {
  const [overlapMessage, setOverlapMessage] = useState<string | null>(null);
  const [overlapMessageSeverity, setOverlapMessageSeverity] = useState<"warning" | "error" | "success" | "info">("warning");
  const [isCheckingOverlap, setIsCheckingOverlap] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [isFetchingUserCount, setIsFetchingUserCount] = useState<boolean>(false);
  const [overlappingRules, setOverlappingRules] = useState<Array<{id: number | string; name: string; rules_meta: any; ranking: number}>>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<RulesFormData>({
    defaultValues: {
      name: null,
      description: null,
      content_id: null,
      min_age: null,
      max_age: null,
      gender: null,
      occupation: null,
      state: null,
      district: null,
      locality: null,
      pincode: null,
      areas_of_interest: [],
      area_of_residence: null,
      caste: null,
      working_status: null,
      family_income: null,
      education_qualification: null,
      work_experience: null,
      differently_abled: '',
      minority_group: '',
      student: '',
      bpl_category: '',
    },
  });

  const pincode = watch("pincode");
  const state = watch("state");
  const district = watch("district");

  // Fetch master data
  const { data: masterData } = useQuery(["masterData"], async () => {
    const response = await getMasterData();
    return response?.data?.data;
  });

  const { data: choicesMasterData } = useQuery(["choicesMasterData"], async () => {
    const response = await getChoicesMasterData();
    return response?.data?.data;
  });

  // const { data: allMasterData } = useQuery(["allMasterData"], async () => {
  //   const response = await getAllMasterData();
  //   return response?.data?.data;
  // });
  

  

  const { data: states } = useQuery(["states"], async () => {
    const response = await getStateMasterData();
    return response?.data?.data?.list || [];
  });

  const { data: districts } = useQuery(
    ["districts", state],
    async () => {
      if (state) {
        const response = await getDistrictMasterData(state);
        return response?.data?.data?.list || [];
      }
      return [];
    },
    { enabled: !!state && !pincode }
  );

  const { data: cities } = useQuery(
    ["cities", state],
    async () => {
      if (state) {
        const response = await getCityMasterData(state);
        return response?.data?.data?.list || [];
      }
      return [];
    },
    { enabled: !!state && !pincode }
  );

  // Fetch landing pages for contents dropdown
  const { data: landingPages } = useQuery(
    ["landingPages"],
    async () => {
      const response = await getLandingPages({
        page: 1,
        pageSize: 1000, // Get all landing pages
      });
      return response?.data?.data?.list || [];
    }
  );

  // Watch all form values and call onDataChange
  const formValues = watch();

  // Watch individual fields to ensure changes are detected
  const watchedFields = {
    name: watch("name"),
    description: watch("description"),
    content_id: watch("content_id"),
    min_age: watch("min_age"),
    max_age: watch("max_age"),
    gender: watch("gender"),
    occupation: watch("occupation"),
    state: watch("state"),
    district: watch("district"),
    locality: watch("locality"),
    pincode: watch("pincode"),
    areas_of_interest: watch("areas_of_interest"),
    area_of_residence: watch("area_of_residence"),
    caste: watch("caste"),
    working_status: watch("working_status"),
    family_income: watch("family_income"),
    education_qualification: watch("education_qualification"),
    work_experience: watch("work_experience"),
    differently_abled: watch("differently_abled"),
    minority_group: watch("minority_group"),
    student: watch("student"),
    bpl_category: watch("bpl_category"),
  };
  

  const normalizeOptionLabel = (opt: any): string => {
    const raw = opt?.value ?? opt?.name ?? opt?.label ?? "";
    return String(raw);
  };

  const isAllLabel = (label: string): boolean => label.trim().toLowerCase() === "all";

  const findOptionById = (id: number | string | null | undefined, options: any[]) => {
    if (id === null || id === undefined) return undefined;
    if (!Array.isArray(options)) return undefined;
    return options.find((opt: any) => opt?.id === id);
  };

  const isAllOption = (opt: any): boolean => {
    if (!opt) return false;
    return isAllLabel(normalizeOptionLabel(opt));
  };

  // Helper function to get display label by id from master data
  const getValueById = (id: number | string | null, options: any[]): string | null => {
    if (id === null || id === undefined || !Array.isArray(options)) return null;
    const item = findOptionById(id, options);
    const label = item ? normalizeOptionLabel(item) : "";
    return label || null;
  };

  // Helper function to get values by ids array
  const getValuesByIds = (ids: number[] | null | undefined, options: any[]): string[] => {
    if (!ids || !Array.isArray(ids) || !options) return [];
    return ids.map(id => {
      const item = findOptionById(id, options);
      return item ? normalizeOptionLabel(item) : "";
    }).filter(Boolean);
  };

  // Transform form data to rules only (for overlap check - send IDs)
  const transformToRulesForOverlap = (data: RulesFormData) => {    
    const rules: any = {};

    if(data.min_age && data.min_age>0){
      rules.min_age=data.min_age
    }
    if(data.max_age && data.max_age>0){
      rules.max_age=data.max_age
    }

    console.log("coming here",data);
    
    if(data.differently_abled=='true' || data.differently_abled=='false'){
      rules.differently_abled=data.differently_abled
    }
    if(data.minority_group=='true' || data.minority_group=='false'){
      rules.minority_group=data.minority_group
    }
    if(data.student=='true' || data.student=='false'){
      rules.student=data.student
    }
    if(data.bpl_category=='true' || data.bpl_category=='false'){
      rules.bpl_category=data.bpl_category
    }
    // Use raw master lists so we can detect "All" by id->label lookup
    const occupationOptions = masterData?.occupation || [];
    const casteOptions = masterData?.caste || [];
    const workingStatusOptions = masterData?.workingStatus || [];
    const familyIncomeOptions = masterData?.familyIncome || [];
    const educationOptions = masterData?.education || [];
    const workExperienceOptions = choicesMasterData?.workExperience || [];
    const areaOfResidenceOptions = masterData?.areaOfResidence || [];
    const areasOfInterestOptions = choicesMasterData?.areasOfInterest || [];

    // Fields with IDs - exclude if "All" selected (aggregate / sentinel / none)
    if (!shouldExcludeField(data.occupation, occupationOptions) && data.occupation !== null && data.occupation !== undefined) {
      rules.occupation = data.occupation;
    }

    if (data.state !== null && data.state !== undefined && !shouldExcludeField(data.state, states || [])) {
      rules.state = data.state;
    }    

    if (!shouldExcludeField(data.district, districts || []) && data.district !== null && data.district !== undefined) {
      rules.district = data.district;
    }

    if (!shouldExcludeField(data.caste, casteOptions) && data.caste !== null && data.caste !== undefined) {
      rules.caste = data.caste;
    }

    if (!shouldExcludeField(data.working_status, workingStatusOptions) && data.working_status !== null && data.working_status !== undefined) {
      rules.working_status = data.working_status;
    }

    if (!shouldExcludeField(data.family_income, familyIncomeOptions) && data.family_income !== null && data.family_income !== undefined) {
      rules.family_income = data.family_income;
    }

    if (!shouldExcludeField(data.education_qualification, educationOptions) && data.education_qualification !== null && data.education_qualification !== undefined) {
      rules.education_qualification = data.education_qualification;
    }

    if (!shouldExcludeField(data.area_of_residence, areaOfResidenceOptions) && data.area_of_residence !== null && data.area_of_residence !== undefined) {
      rules.area_of_residence = data.area_of_residence;
    }

    if (!shouldExcludeField(data.areas_of_interest, areasOfInterestOptions, true) && data.areas_of_interest && data.areas_of_interest.length > 0) {
      rules.areas_of_interest = data.areas_of_interest;
    }

    // Gender - the detect-overlap and user-count endpoints both expect the
    // numeric id (unlike transformToPayload's rules/rules_meta split, which
    // wants the string label); omit if selected option label is "All".
    if (data.gender !== null && data.gender !== undefined && !shouldExcludeField(data.gender, masterData?.gender || [])) {
      if (typeof data.gender === "number") {
        rules.gender = data.gender;
      } else {
        // data.gender can hold a label instead of an id (e.g. prefilled
        // from an existing rule's saved value) - resolve it back to the id.
        const match = (masterData?.gender || []).find(
          (opt: any) => normalizeOptionLabel(opt).toLowerCase() === String(data.gender).toLowerCase()
        );
        if (match) rules.gender = match.id;
      }
    }

    if (data.locality !== null && data.locality !== undefined && data.locality !== "") {
      rules.locality = data.locality;
    }

    if (data.pincode !== null && data.pincode !== undefined && data.pincode !== "") {
      rules.pincode = data.pincode;
    }

    if (data.work_experience !== null && data.work_experience !== undefined && !shouldExcludeField(data.work_experience, workExperienceOptions)) {
      rules.work_experience = data.work_experience;
    }

    return rules;
  };

  // Helper: treat explicit "All" selection as "not a rule", so we omit it from payload.
  // Note: implemented as function declarations so they can be used above (e.g., in overlap transform).
  function isAllSelected(selectedIds: number[] | null | undefined, allOptions: any[]): boolean {
    if (!selectedIds || selectedIds.length === 0) return true; // None selected = "All"
    if (!Array.isArray(allOptions) || allOptions.length === 0) return false;

    // If the explicit "All" option is selected, treat as All
    if (selectedIds.some((id) => isAllOption(findOptionById(id, allOptions)))) return true;

    // Treat selecting all real (non-"All") options as All
    const realOptions = allOptions.filter((opt: any) => !isAllOption(opt));
    if (realOptions.length === 0) return false;
    return realOptions.every((opt: any) => selectedIds.includes(opt.id));
  }

  function shouldExcludeField(fieldValue: any, allOptions: any[], isMultiSelect: boolean = false): boolean {
    if (isMultiSelect) {
      return isAllSelected(fieldValue, allOptions);
    }

    if (fieldValue === null || fieldValue === undefined) return true;
    if (typeof fieldValue === "string" && fieldValue.trim().toLowerCase() === "all") return true;

    // If we received an id and it maps to an "All" label in master data, omit it
    if (Array.isArray(allOptions) && allOptions.length > 0) {
      const opt = findOptionById(fieldValue, allOptions);
      if (opt && isAllOption(opt)) return true;
    }

    return false;
  }

  // Transform form data to final payload structure
  const transformToPayload = (data: RulesFormData) => {
    const rules: any = {};
    const rules_meta: any = {};

    // Fields with IDs that have "All" option - exclude if "All" selected or none selected
    const occupationOptions = masterData?.occupation || [];
    if (!shouldExcludeField(data.occupation, occupationOptions) && data.occupation !== null && data.occupation !== undefined) {
      rules.occupation = data.occupation;
      rules_meta.occupation = getValueById(data.occupation, occupationOptions) || "";
    }

    // State and District: omit if the selected id maps to an option whose label is "All"
    if (data.state !== null && data.state !== undefined && !shouldExcludeField(data.state, states || [])) {
      rules.state = data.state;
      rules_meta.state = getValueById(data.state, states || []) || "";
    }

    if (!shouldExcludeField(data.district, districts || []) && data.district !== null && data.district !== undefined) {
      rules.district = data.district;
      rules_meta.district = getValueById(data.district, districts || []) || "";
    }

    const casteOptions = masterData?.caste || [];
    if (!shouldExcludeField(data.caste, casteOptions) && data.caste !== null && data.caste !== undefined) {
      rules.caste = data.caste;
      rules_meta.caste = getValueById(data.caste, casteOptions) || "";
    }

    const workingStatusOptions = masterData?.workingStatus || [];
    if (!shouldExcludeField(data.working_status, workingStatusOptions) && data.working_status !== null && data.working_status !== undefined) {
      rules.working_status = data.working_status;
      rules_meta.working_status = getValueById(data.working_status, workingStatusOptions) || "";
    }

    const familyIncomeOptions = masterData?.familyIncome || [];
    if (!shouldExcludeField(data.family_income, familyIncomeOptions) && data.family_income !== null && data.family_income !== undefined) {
      rules.family_income = data.family_income;
      rules_meta.family_income = getValueById(data.family_income, familyIncomeOptions) || "";
    }

    const educationOptions = masterData?.education || [];
    if (!shouldExcludeField(data.education_qualification, educationOptions) && data.education_qualification !== null && data.education_qualification !== undefined) {
      rules.education_qualification = data.education_qualification;
      rules_meta.education_qualification = getValueById(data.education_qualification, educationOptions) || "";
    }

    const areaOfResidenceOptions = masterData?.areaOfResidence || [];
    if (!shouldExcludeField(data.area_of_residence, areaOfResidenceOptions) && data.area_of_residence !== null && data.area_of_residence !== undefined) {
      rules.area_of_residence = data.area_of_residence;
      rules_meta.area_of_residence = getValueById(data.area_of_residence, areaOfResidenceOptions) || "";
    }

    // Areas of Interest - multi-select field with "All" option
    const areasOfInterestOptions = choicesMasterData?.areasOfInterest || [];
    if (!shouldExcludeField(data.areas_of_interest, areasOfInterestOptions, true) && 
        data.areas_of_interest && data.areas_of_interest.length > 0) {
      rules.areas_of_interest = data.areas_of_interest;
      rules_meta.areas_of_interest = getValuesByIds(data.areas_of_interest, areasOfInterestOptions);
    }

    // Gender - rules.gender is the numeric id (backend schema: integer),
    // rules_meta.gender is the display label; if selected option is "All", omit.
    if (data.gender !== null && data.gender !== undefined && !shouldExcludeField(data.gender, masterData?.gender || [])) {
      if (typeof data.gender === "number") {
        rules.gender = data.gender;
        rules_meta.gender = getValueById(data.gender, masterData?.gender || []) || "";
      } else {
        // data.gender can hold a label instead of an id (e.g. prefilled
        // from an existing rule's saved value) - resolve it back to the id.
        const match = (masterData?.gender || []).find(
          (opt: any) => normalizeOptionLabel(opt).toLowerCase() === String(data.gender).toLowerCase()
        );
        if (match) {
          rules.gender = match.id;
          rules_meta.gender = normalizeOptionLabel(match);
        }
      }
    }

    // Fields without IDs - same value in both rules and rules_meta (only if value exists)
    if (data.locality !== null && data.locality !== undefined && data.locality !== "") {
      rules.locality = data.locality;
      rules_meta.locality = data.locality;
    }

    if (data.pincode !== null && data.pincode !== undefined && data.pincode !== "") {
      rules.pincode = data.pincode;
      rules_meta.pincode = data.pincode;
    }

    if (data.min_age !== null && data.min_age !== undefined && data.min_age > 0) {
      rules.min_age = data.min_age;
      rules_meta.min_age = data.min_age;
    }

    if (data.max_age !== null && data.max_age !== undefined && data.max_age > 0) {
      rules.max_age = data.max_age;
      rules_meta.max_age = data.max_age;
    }

    const workExperienceOptions = choicesMasterData?.workExperience || [];
    if (data.work_experience !== null && data.work_experience !== undefined && !shouldExcludeField(data.work_experience, workExperienceOptions)) {
      rules.work_experience = data.work_experience;
      rules_meta.work_experience = getValueById(data.work_experience, workExperienceOptions) || "";
    }

    // Boolean-like fields (Yes/No/All): form may store boolean or string "true"/"false"/"all"
    // - if "all" or empty, omit from payload
    // - if true/false or "true"/"false", include as boolean
    const toBoolOrUndefined = (v: boolean | string | null | undefined): boolean | undefined => {
      if (v === true || v === "true") return true;
      if (v === false || v === "false") return false;
      return undefined;
    };
    const dAbled = toBoolOrUndefined(data.differently_abled);
    if (dAbled !== undefined) {
      rules.differently_abled = dAbled;
      rules_meta.differently_abled = dAbled;
    }
    const minGrp = toBoolOrUndefined(data.minority_group);
    if (minGrp !== undefined) {
      rules.minority_group = minGrp;
      rules_meta.minority_group = minGrp;
    }
    const stud = toBoolOrUndefined(data.student);
    if (stud !== undefined) {
      rules.student = stud;
      rules_meta.student = stud;
    }
    const bpl = toBoolOrUndefined(data.bpl_category);
    if (bpl !== undefined) {
      rules.bpl_category = bpl;
      rules_meta.bpl_category = bpl;
    }

    // Build ranking_updates from overlapping rules
    const ranking_updates = overlappingRules.map((rule, index) => ({
      id: rule.id.toString(), // Convert to string as per API spec
      ranking: index + 1, // Number, not string
    }));

    // Find current rule's ranking
    const currentRuleRanking = currentRuleId 
      ? overlappingRules.findIndex((r) => r.id === currentRuleId) + 1
      : (overlappingRules.findIndex((r) => r.id === "new_rule") + 1);

      
    const payload: any = {
      name: data.name || "",
      description: data.description || "",
      content_id: data.content_id || 0,
      rules,
      rules_meta,
    };

    // Include ID when editing (to update existing rule instead of creating new one)
    if (currentRuleId) {
      payload.id = currentRuleId;
    }

    // Include ranking and ranking_updates if there are overlapping rules
    if (ranking_updates.length > 0) {
      // Include current rule's ranking (0 if not found in list)
      payload.ranking = currentRuleRanking > 0 ? currentRuleRanking : 0;
      payload.ranking_updates = ranking_updates;
    } else {
      // If no overlapping rules, set ranking to 0
      payload.ranking = 0;
    }

    return payload;
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newRules = [...overlappingRules];
    const draggedItem = newRules[draggedIndex];
    newRules.splice(draggedIndex, 1);
    newRules.splice(dropIndex, 0, draggedItem);
    
    // Update rankings based on new order
    const updatedRules = newRules.map((rule, index) => ({
      ...rule,
      ranking: index + 1,
    }));

    setOverlappingRules(updatedRules);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Debounce timer for overlap check
  const overlapCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Debounce timer for user count check
  const userCountTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check rule overlap whenever form values change (similar to user-count)
  useEffect(() => {
    // Skip check if viewMode
    if (viewMode) {
      setOverlapMessage(null);
      setSnackbarOpen(false);
      return;
    }

    // Clear previous timer
    if (overlapCheckTimerRef.current) {
      clearTimeout(overlapCheckTimerRef.current);
    }

    // Debounce the API call - always call when fields change
    overlapCheckTimerRef.current = setTimeout(async () => {
      try {
        setIsCheckingOverlap(true);
        // Use watchedFields to create the form data object for transformation
        const formDataForTransform: RulesFormData = {
          name: watchedFields.name,
          description: watchedFields.description,
          content_id: watchedFields.content_id,
          min_age: watchedFields.min_age,
          max_age: watchedFields.max_age,
          gender: watchedFields.gender,
          occupation: watchedFields.occupation,
          state: watchedFields.state,
          district: watchedFields.district,
          locality: watchedFields.locality,
          pincode: watchedFields.pincode,
          areas_of_interest: watchedFields.areas_of_interest,
          area_of_residence: watchedFields.area_of_residence,
          caste: watchedFields.caste,
          working_status: watchedFields.working_status,
          family_income: watchedFields.family_income,
          education_qualification: watchedFields.education_qualification,
          work_experience: watchedFields.work_experience,
          differently_abled: watchedFields.differently_abled,
          minority_group: watchedFields.minority_group,
          student: watchedFields.student,
          bpl_category: watchedFields.bpl_category,
        };
        const rulesPayload = transformToRulesForOverlap(formDataForTransform);

        const response = await checkRuleOverlap(rulesPayload);
        
        // Check if response indicates an error
        if (response?.data?.success === false || response?.data?.error) {
          const errorMessage = response?.data?.error?.message || response?.data?.message || "An error occurred while checking rule overlap";
          setOverlapMessage(errorMessage);
          setOverlapMessageSeverity("error");
          setSnackbarOpen(true);
          setOverlappingRules([]);
        } else if (response?.data?.data?.overlapped) {
          const overlapMsg = "Rules are overlapping";
          setOverlapMessage(overlapMsg);
          setOverlapMessageSeverity("warning");
          setSnackbarOpen(true);
          // Store overlapping rules from API - response.data.data should contain the array of rules
          const rules = Array.isArray(response?.data?.data) 
            ? response?.data?.data 
            : (response?.data?.data?.allRules || []);
          
          // Check if current rule is already in the list (when editing)
          const currentRuleInList = currentRuleId 
            ? rules.find((r: any) => r.id === currentRuleId)
            : null;
          
          // If editing and rule is not in the list, add it; if adding new, add "new_rule"
          let rulesWithCurrentRule;
          if (currentRuleId && !currentRuleInList) {
            // Editing mode - add current rule to the list
            rulesWithCurrentRule = [
              ...rules,
              { id: currentRuleId, name: watchedFields.name || "Current Rule", rules_meta: {}, ranking: rules.length + 1 }
            ];
          } else if (!currentRuleId) {
            // Adding new rule
            rulesWithCurrentRule = [
              ...rules,
              { id: "new_rule", name: "new_rule", rules_meta: {}, ranking: rules.length + 1 }
            ];
          } else {
            // Current rule already in the list (editing existing rule that overlaps)
            rulesWithCurrentRule = rules;
          }
          if(!overlappingRules.length){
            setOverlappingRules(rulesWithCurrentRule);
          }
        } else {
          setOverlapMessage(null);
          setSnackbarOpen(false);
          setOverlappingRules([]);
        }
      } catch (error: any) {
        console.error("Failed to check rule overlap:", error);
        // Extract error message from API response
        const errorMessage = error?.response?.data?.error?.message || 
                           error?.response?.data?.message || 
                           error?.message || 
                           "An error occurred while checking rule overlap";
        setOverlapMessage(errorMessage);
        setOverlapMessageSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsCheckingOverlap(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (overlapCheckTimerRef.current) {
        clearTimeout(overlapCheckTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedFields.name,
    watchedFields.description,
    watchedFields.content_id,
    watchedFields.min_age,
    watchedFields.max_age,
    watchedFields.gender,
    watchedFields.occupation,
    watchedFields.state,
    watchedFields.district,
    watchedFields.locality,
    watchedFields.pincode,
    watchedFields.areas_of_interest,
    watchedFields.area_of_residence,
    watchedFields.caste,
    watchedFields.working_status,
    watchedFields.family_income,
    watchedFields.education_qualification,
    watchedFields.work_experience,
    watchedFields.differently_abled,
    watchedFields.minority_group,
    watchedFields.student,
    watchedFields.bpl_category,
    viewMode,
    masterData,
    states,
    districts,
    choicesMasterData
  ]);

  // Fetch user count whenever form values change
  useEffect(() => {
    // Skip check if viewMode
    if (viewMode) {
      setUserCount(null);
      return;
    }

    // Clear previous timer
    if (userCountTimerRef.current) {
      clearTimeout(userCountTimerRef.current);
    }

    // Debounce the API call - always call since we include required fields
    userCountTimerRef.current = setTimeout(async () => {
      try {
        setIsFetchingUserCount(true);
        // Use watchedFields to create the form data object for transformation
        const formDataForTransform: RulesFormData = {
          name: watchedFields.name,
          description: watchedFields.description,
          content_id: watchedFields.content_id,
          min_age: watchedFields.min_age,
          max_age: watchedFields.max_age,
          gender: watchedFields.gender,
          occupation: watchedFields.occupation,
          state: watchedFields.state,
          district: watchedFields.district,
          locality: watchedFields.locality,
          pincode: watchedFields.pincode,
          areas_of_interest: watchedFields.areas_of_interest,
          area_of_residence: watchedFields.area_of_residence,
          caste: watchedFields.caste,
          working_status: watchedFields.working_status,
          family_income: watchedFields.family_income,
          education_qualification: watchedFields.education_qualification,
          work_experience: watchedFields.work_experience,
          differently_abled: watchedFields.differently_abled,
          minority_group: watchedFields.minority_group,
          student: watchedFields.student,
          bpl_category: watchedFields.bpl_category,
        };
        const rulesPayload = transformToRulesForOverlap(formDataForTransform);

        const response = await getUserCountForRule(rulesPayload);
        
        if (response?.data?.success && response?.data?.data?.userCount !== undefined) {
          setUserCount(response.data.data.userCount);
        } else {
          setUserCount(null);
        }
      } catch (error: any) {
        console.error("Failed to fetch user count:", error);
        setUserCount(null);
      } finally {
        setIsFetchingUserCount(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (userCountTimerRef.current) {
        clearTimeout(userCountTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedFields.name,
    watchedFields.description,
    watchedFields.content_id,
    watchedFields.min_age,
    watchedFields.max_age,
    watchedFields.gender,
    watchedFields.occupation,
    watchedFields.state,
    watchedFields.district,
    watchedFields.locality,
    watchedFields.pincode,
    watchedFields.areas_of_interest,
    watchedFields.area_of_residence,
    watchedFields.caste,
    watchedFields.working_status,
    watchedFields.family_income,
    watchedFields.education_qualification,
    watchedFields.work_experience,
    watchedFields.differently_abled,
    watchedFields.minority_group,
    watchedFields.student,
    watchedFields.bpl_category,
    viewMode,
    masterData,
    states,
    districts,
    choicesMasterData
  ]);

  useEffect(() => {
    // Build form data from watchedFields
    const formData: RulesFormData = {
      name: watchedFields.name,
      description: watchedFields.description,
      content_id: watchedFields.content_id,
      min_age: watchedFields.min_age,
      max_age: watchedFields.max_age,
      gender: watchedFields.gender,
      occupation: watchedFields.occupation,
      state: watchedFields.state,
      district: watchedFields.district,
      locality: watchedFields.locality,
      pincode: watchedFields.pincode,
      areas_of_interest: watchedFields.areas_of_interest,
      area_of_residence: watchedFields.area_of_residence,
      caste: watchedFields.caste,
      working_status: watchedFields.working_status,
      family_income: watchedFields.family_income,
      education_qualification: watchedFields.education_qualification,
      work_experience: watchedFields.work_experience,
      differently_abled: watchedFields.differently_abled,
      minority_group: watchedFields.minority_group,
      student: watchedFields.student,
      bpl_category: watchedFields.bpl_category,
    };
    
    const payload = transformToPayload(formData);
    onDataChange(payload as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedFields.name,
    watchedFields.description,
    watchedFields.content_id,
    watchedFields.min_age,
    watchedFields.max_age,
    watchedFields.gender,
    watchedFields.occupation,
    watchedFields.state,
    watchedFields.district,
    watchedFields.locality,
    watchedFields.pincode,
    watchedFields.areas_of_interest,
    watchedFields.area_of_residence,
    watchedFields.caste,
    watchedFields.working_status,
    watchedFields.family_income,
    watchedFields.education_qualification,
    watchedFields.work_experience,
    watchedFields.differently_abled,
    watchedFields.minority_group,
    watchedFields.student,
    watchedFields.bpl_category,
    masterData,
    states,
    districts,
    choicesMasterData,
    overlappingRules, // Include overlappingRules since transformToPayload uses it
  ]);

  // Initialize form with initialData (only when rule ID changes, not on every render)
  const previousRuleId = useRef<number | null>(null);
  
  useEffect(() => {
    // Only initialize if:
    // 1. We have initialData AND
    // 2. We haven't initialized for this rule yet (rule ID changed or first time)
    const ruleIdChanged = currentRuleId !== previousRuleId.current;
    const shouldInitialize = initialData && (ruleIdChanged || (!currentRuleId && previousRuleId.current === null));
    
    if (shouldInitialize) {
      // Merge initialData with default values to ensure all fields are set
      const formData: RulesFormData = {
        name: initialData.name ?? null,
        description: initialData.description ?? null,
        content_id: initialData.content_id ?? null,
        min_age: initialData.min_age ?? null,
        max_age: initialData.max_age ?? null,
        gender: initialData.gender ?? null,
        occupation: initialData.occupation ?? null,
        state: initialData.state ?? null,
        district: initialData.district ?? null,
        locality: initialData.locality ?? null,
        pincode: initialData.pincode ?? null,
        areas_of_interest: initialData.areas_of_interest ?? [],
        area_of_residence: initialData.area_of_residence ?? null,
        caste: initialData.caste ?? null,
        working_status: initialData.working_status ?? null,
        family_income: initialData.family_income ?? null,
        education_qualification: initialData.education_qualification ?? null,
        work_experience: initialData.work_experience ?? null,
        differently_abled: typeof initialData.differently_abled === 'boolean' || initialData.differently_abled === 'all' ? initialData.differently_abled : '',
        minority_group: typeof initialData.minority_group === 'boolean' || initialData.minority_group === 'all' ? initialData.minority_group : '',
        student: typeof initialData.student === 'boolean' || initialData.student === 'all' ? initialData.student : '',
        bpl_category: typeof initialData.bpl_category === 'boolean' || initialData.bpl_category === 'all' ? initialData.bpl_category : '',
      };
      reset(formData);
      previousRuleId.current = currentRuleId;
      setOverlappingRules([]);
    } else if (!initialData && previousRuleId.current !== null) {
      // Reset when switching from edit to add mode
      previousRuleId.current = null;
      setOverlappingRules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRuleId]); // Only depend on currentRuleId to avoid re-initializing on every render

  // Disable state/district/locality when pincode is entered
  const isLocationDisabled = !!pincode;

  // Clear state/district when pincode is entered
  useEffect(() => {
    if (pincode) {
      setValue("state", null);
      setValue("district", null);
      setValue("locality", null);
    }
  }, [pincode, setValue]);

  // Use gender from masterData, filtering out aggregates - then add an
  // explicit "All" entry (id: null, same as leaving the field unselected)
  // so it's a visible choice instead of an implicit "clear the field" action.
  const genderOptions = [
    { id: null, value: "All" },
    ...(masterData?.gender?.filter((item: any) => !item?.isAggregate && !item?.is_aggregate) || []),
  ];

  return (
    <Box sx={{ mt: 2, position: "relative" }}>
      <UserCountDisplay
        userCount={userCount}
        isFetchingUserCount={isFetchingUserCount}
        viewMode={viewMode}
      />

      <Grid container spacing={3}>
        {/* Main Form Content - Left Side */}
        <Grid item xs={12} md={overlappingRules.length > 0 && !viewMode ? 8 : 12}>
          <ContentInformationSection
            control={control}
            viewMode={viewMode}
            landingPages={landingPages || []}
          />

          <RulesSection
            control={control}
            setValue={setValue}
            viewMode={viewMode}
            masterData={masterData}
            states={states || []}
            districts={districts || []}
            choicesMasterData={choicesMasterData}
            isLocationDisabled={isLocationDisabled}
            genderOptions={genderOptions}
          />
        </Grid>

        {/* Drag and Drop Rules Ranking - Right Side */}
        {!viewMode && overlappingRules.length > 0 && (
          <Grid item xs={12} md={4}>
            <RulesRankingList
              overlappingRules={overlappingRules}
              currentRuleId={currentRuleId}
              draggedIndex={draggedIndex}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              viewMode={viewMode}
          />
        </Grid>
        )}
      </Grid>

      {/* Snackbar for overlap warnings/errors */}
      <SnackbarComp
        open={snackbarOpen && !!overlapMessage}
        message={overlapMessage || ""}
        severity={overlapMessageSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
